/**
 * BoltDataCard — Renders structured context data returned alongside BOLT responses.
 *
 * Adaptive rendering tiers:
 *   1-5  items → compact inline list
 *   6-15 items → collapsed with expand/collapse toggle
 *   15+  items → summary count with "View All" toggle
 */

import React, { useState } from 'react';
import {
  Users,
  AlertTriangle,
  GraduationCap,
  Clock,
  Building2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import './BoltDataCard.css';

const TYPE_CONFIG = {
  workers: { icon: Users, label: 'Workers', color: 'var(--accent-primary, #6366f1)' },
  hazards: { icon: AlertTriangle, label: 'Hazard Summary', color: 'var(--accent-warning, #f59e0b)' },
  training: { icon: GraduationCap, label: 'Training Status', color: 'var(--accent-info, #38bdf8)' },
  attendance: { icon: Clock, label: 'Shift Attendance', color: 'var(--accent-success, #22c55e)' },
  org: { icon: Building2, label: 'Organization', color: 'var(--accent-primary, #6366f1)' },
};

const COMPACT_LIMIT = 5;
const COLLAPSED_LIMIT = 15;
const COLLAPSED_PREVIEW = 5;

function DataItem({ item, type }) {
  if (type === 'workers') {
    return (
      <div className="bolt-data-card__item">
        <span className="bolt-data-card__item-primary">{item.name}</span>
        {(item.role || item.trade) && (
          <span className="bolt-data-card__item-secondary">
            {item.role}{item.role && item.trade ? ' · ' : ''}{item.trade}
          </span>
        )}
      </div>
    );
  }

  if (type === 'hazards') {
    return (
      <div className="bolt-data-card__item bolt-data-card__item--hazard">
        <span className="bolt-data-card__item-primary">{item.name || item.hazard}</span>
        {item.severity && (
          <span className={`bolt-data-card__severity bolt-data-card__severity--${item.severity}`}>
            {item.severity}
          </span>
        )}
      </div>
    );
  }

  if (type === 'training') {
    return (
      <div className="bolt-data-card__item">
        <span className="bolt-data-card__item-primary">{item.name}</span>
        <span className={`bolt-data-card__status bolt-data-card__status--${item.status}`}>
          {item.status}
        </span>
      </div>
    );
  }

  if (type === 'attendance') {
    return (
      <div className="bolt-data-card__item">
        <span className="bolt-data-card__item-primary">{item.name}</span>
        <span className="bolt-data-card__item-secondary">
          {item.checkIn || item.status || '—'}
        </span>
      </div>
    );
  }

  // Fallback / org
  return (
    <div className="bolt-data-card__item">
      <span className="bolt-data-card__item-primary">
        {item.name || item.label || JSON.stringify(item)}
      </span>
      {item.value && (
        <span className="bolt-data-card__item-secondary">{item.value}</span>
      )}
    </div>
  );
}

export default function BoltDataCard({ type, label, items }) {
  const [expanded, setExpanded] = useState(false);

  if (!items?.length) return null;

  const config = TYPE_CONFIG[type] || TYPE_CONFIG.workers;
  const Icon = config.icon;
  const title = label || config.label;
  const count = items.length;

  const isTierCompact = count <= COMPACT_LIMIT;
  const isTierCollapsed = count > COMPACT_LIMIT && count <= COLLAPSED_LIMIT;
  const isTierLarge = count > COLLAPSED_LIMIT;

  let visibleItems;
  if (isTierCompact || expanded) {
    visibleItems = items;
  } else {
    visibleItems = items.slice(0, COLLAPSED_PREVIEW);
  }

  const hiddenCount = count - visibleItems.length;

  return (
    <div className="bolt-data-card" role="region" aria-label={title}>
      <div className="bolt-data-card__header">
        <Icon size={14} style={{ color: config.color }} aria-hidden="true" />
        <span className="bolt-data-card__title">{title}</span>
        <span className="bolt-data-card__count">{count}</span>
      </div>

      <div className="bolt-data-card__items">
        {visibleItems.map((item, i) => (
          <DataItem key={item.id || i} item={item} type={type} />
        ))}
      </div>

      {(isTierCollapsed || isTierLarge) && (
        <button
          className="bolt-data-card__toggle"
          onClick={() => setExpanded((prev) => !prev)}
          type="button"
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              <ChevronUp size={14} /> Collapse
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              {isTierLarge && !expanded
                ? `View All (${hiddenCount} more)`
                : `Show ${hiddenCount} more`}
            </>
          )}
        </button>
      )}
    </div>
  );
}
